/**
 * EditorAiCapability — granular, model-aware, *validating* tools that an AI
 * assistant (hosted by the admin app) can invoke against THIS workflow editor.
 *
 * Design contract (defined locally / structurally so the admin can consume it
 * without importing editor internals):
 *
 *   interface EditorAiCapability {
 *     kind: string;
 *     guidelines: string;
 *     tools: { name; description; parameters }[];   // parameters = JSON Schema object
 *     invoke(toolName, args): Promise<EditorToolResult>;
 *   }
 *   interface EditorToolResult { ok; result?; rejected?; reason? }
 *
 * This module is intentionally PURE / React-free: it operates on a small
 * `WorkflowModelAccess` abstraction that the React hook fills in from the live
 * editor state + the editor's REAL mutation/validation functions. Every tool
 * routes through that real path, so invalid input (unknown type, bad
 * connection, missing required field, malformed JSON) returns
 * `{ ok:false, rejected:true, reason }` INSTEAD of corrupting the model.
 *
 * Keeping the logic here (not inside the hook) makes it unit-testable without a
 * DOM or React renderer.
 */

import { AppNode, Edge, NodeTypeConfig, WorkflowNodeData, Swimlane } from '../types';
import { ValidationIssue } from './validation';

// --- Contract types (structural; mirror what the admin host expects) ---

export interface EditorToolDescriptor {
  name: string;
  description: string;
  /** JSON Schema object describing the tool's arguments. */
  parameters: Record<string, unknown>;
}

export interface EditorToolResult {
  ok: boolean;
  result?: unknown;
  rejected?: boolean;
  reason?: string;
}

export interface EditorAiCapability {
  kind: string;
  guidelines: string;
  tools: EditorToolDescriptor[];
  invoke(toolName: string, args: unknown): Promise<EditorToolResult>;
}

// --- Model access the hook must supply (bound to live editor state) ---

/**
 * A thin window onto the editor's live model + its REAL mutators. The hook
 * implements this against `useWorkflowEditor`'s nodes/edges + the registry +
 * setNodes/setEdges/onConnect/validateDiagram. The pure core below never
 * touches React.
 */
export interface WorkflowModelAccess {
  /** Live nodes of the active process (latest snapshot). */
  getNodes(): AppNode[];
  /** Live edges of the active process. */
  getEdges(): Edge[];
  /** The editor's node-type registry (key -> config). Single source of truth. */
  getNodeTypes(): Record<string, NodeTypeConfig>;
  /** Registry default data for a type (applied on add), if any. */
  getDefaultData(type: string): Partial<WorkflowNodeData> | undefined;

  /** Apply a functional update to nodes (the editor's real setNodes). */
  applyNodes(updater: (nodes: AppNode[]) => AppNode[]): void;
  /** Apply a functional update to edges (the editor's real setEdges). */
  applyEdges(updater: (edges: Edge[]) => Edge[]): void;

  /**
   * Run the editor's REAL validation over a CANDIDATE diagram. The hook merges
   * the supplied nodes/edges into the active process and runs `validateDiagram`
   * against the whole project (so cross-process / ruleset rules still fire).
   * Passing the current nodes/edges yields the baseline; passing a proposed
   * next model yields the candidate's issues. Mutating tools compute both and
   * only apply when the candidate introduces no NEW error-severity issues.
   */
  validate(nodes: AppNode[], edges: Edge[]): ValidationIssue[];

  /**
   * OPTIONAL viewport/selection/UI surface, supplied by the React hook (it needs
   * `useReactFlow`, selection setters, modal + process state — things the pure
   * model layer has none of). When absent, the navigation/selection tools are
   * not advertised and invoking them rejects gracefully. None of these mutate
   * the serialized model; they only move the camera / change UI selection state.
   */
  view?: ViewAccess;
}

/** A read-only snapshot of the live React Flow viewport. */
export interface ViewportSnapshot {
  x: number;
  y: number;
  zoom: number;
}

/** The active selection in the editor (mutually exclusive node|edge|swimlane). */
export interface SelectionSnapshot {
  kind: 'node' | 'edge' | 'swimlane' | 'none';
  id: string | null;
}

/**
 * Live editor view/UI access. Every method routes through the editor's REAL
 * React Flow instance + selection/modal/process state. Implemented by the hook.
 */
export interface ViewAccess {
  /** Current swimlanes of the active process. */
  getSwimlanes(): Swimlane[];
  /** Process summaries (id + name + active flag) for switch_process / get_ui_state. */
  getProcesses(): { id: string; name: string; active: boolean }[];
  /** Current React Flow viewport (pan + zoom). */
  getViewport(): ViewportSnapshot;
  /** Current selection (node / edge / swimlane / none). */
  getSelection(): SelectionSnapshot;
  /** Whether the advanced node modal is open, and on which tab. */
  getModalState(): { open: boolean; nodeId: string | null; tabId: string | null };

  /** Pan/zoom to an exact viewport. */
  setViewport(v: ViewportSnapshot, durationMs?: number): void;
  /** Fit the given subset (or everything when empty) into view. */
  fitView(opts: { nodeIds?: string[]; padding?: number; durationMs?: number; minZoom?: number; maxZoom?: number }): void;
  /** Center the camera on a node, optionally choosing the zoom. */
  centerNode(nodeId: string, zoom?: number, durationMs?: number): void;
  /** Select a node (and clear other selection sources). */
  selectNode(nodeId: string): void;
  /** Select an edge. */
  selectEdge(edgeId: string): void;
  /** Select a swimlane. */
  selectSwimlane(swimlaneId: string): void;
  /** Clear all selection. */
  clearSelection(): void;
  /** Open the advanced node modal on a given tab (defaults to 'properties'). */
  openNodeEditor(nodeId: string, tabId?: string): void;
  /** Switch the active process. */
  switchProcess(processId: string): void;
}

// --- Helpers ---

const ok = (result?: unknown): EditorToolResult => ({ ok: true, result });
const reject = (reason: string): EditorToolResult => ({ ok: false, rejected: true, reason });

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Compact node view returned by get_model (drops transient/UI-only fields). */
const compactNode = (n: AppNode) => ({
  id: n.id,
  type: n.type,
  label: n.data?.label,
  actorType: n.data?.actorType,
  parentNode: n.parentNode,
  position: n.position,
});

const compactEdge = (e: Edge) => ({
  id: e.id,
  source: e.source,
  target: e.target,
  sourceHandle: e.sourceHandle ?? undefined,
  targetHandle: e.targetHandle ?? undefined,
  label: typeof e.label === 'string' ? e.label : undefined,
});

const compactIssue = (i: ValidationIssue) => ({
  severity: i.severity,
  rule: i.rule,
  message: i.message,
  nodeIds: i.nodeIds,
  edgeIds: i.edgeIds,
});

let edgeSeq = 0;
const nextId = (prefix: string): string =>
  `${prefix}_${Date.now().toString(36)}_${(edgeSeq++).toString(36)}`;

/** Stable signature of an error-severity issue for baseline/candidate diffing. */
const errorKey = (i: ValidationIssue): string =>
  `${i.rule}|${i.message}|${(i.nodeIds ?? []).join(',')}|${(i.edgeIds ?? []).join(',')}`;

/**
 * Diff the candidate model's validation against the baseline and return the
 * error-severity issues the candidate INTRODUCES. A pre-existing error (the
 * diagram was already invalid, e.g. half-built with no StartEvent) is NOT held
 * against a mutation — only NEW errors block it. Returns [] when the candidate
 * adds no new errors.
 */
const newErrors = (baseline: ValidationIssue[], candidate: ValidationIssue[]): ValidationIssue[] => {
  const baseKeys = new Set(baseline.filter((i) => i.severity === 'error').map(errorKey));
  return candidate.filter((i) => i.severity === 'error' && !baseKeys.has(errorKey(i)));
};

/** Render introduced errors into a single human-readable rejection reason. */
const formatErrors = (issues: ValidationIssue[]): string =>
  issues.map((i) => `[${i.rule}] ${i.message}`).join(' ');

// --- Tool descriptors (JSON Schema) ---

const TOOL_DESCRIPTORS: EditorToolDescriptor[] = [
  {
    name: 'get_model',
    description:
      'Return the current workflow process as compact JSON: an array of nodes (id, type, label, actorType, parentNode, position) and edges (id, source, target, label).',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'list_node_types',
    description:
      'List every node/element type this editor supports, with its category, display label and the engine type it serializes to. Use these exact type keys when adding nodes.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'add_node',
    description:
      'Add a new node. `type` must be one of the keys from list_node_types. `label` is required. `position` {x,y} is optional (defaults near origin). Rejects unknown types and blank labels.',
    parameters: {
      type: 'object',
      properties: {
        type: { type: 'string', description: 'Node type key (see list_node_types).' },
        label: { type: 'string', description: 'Human-readable display label.' },
        position: {
          type: 'object',
          properties: { x: { type: 'number' }, y: { type: 'number' } },
          required: ['x', 'y'],
          additionalProperties: false,
        },
        actorType: { type: 'string', description: 'Optional swimlane/actor name.' },
        data: { type: 'object', description: 'Optional extra node data fields.' },
      },
      required: ['type', 'label'],
      additionalProperties: false,
    },
  },
  {
    name: 'update_node',
    description:
      "Update a node's data fields (e.g. label, description, config). Merges into existing data. Rejects unknown node ids and non-object data.",
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        data: { type: 'object', description: 'Partial data fields to merge.' },
      },
      required: ['id', 'data'],
      additionalProperties: false,
    },
  },
  {
    name: 'delete_node',
    description:
      'Delete a node by id. Also removes its attached edges and any child (boundary) nodes. Rejects unknown node ids.',
    parameters: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
      additionalProperties: false,
    },
  },
  {
    name: 'connect_nodes',
    description:
      'Create a sequence-flow edge from `source` to `target` node id. Optional `label`. Rejects unknown endpoints, self-loops, and duplicate connections.',
    parameters: {
      type: 'object',
      properties: {
        source: { type: 'string' },
        target: { type: 'string' },
        label: { type: 'string' },
      },
      required: ['source', 'target'],
      additionalProperties: false,
    },
  },
  {
    name: 'delete_edge',
    description: 'Delete an edge by id. Rejects unknown edge ids.',
    parameters: {
      type: 'object',
      properties: { id: { type: 'string' } },
      required: ['id'],
      additionalProperties: false,
    },
  },
  {
    name: 'validate',
    description:
      'Run the editor validation rules over the whole diagram and return the list of issues (severity, rule, message, affected node/edge ids).',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'replace_model',
    description:
      'Replace the active process nodes + edges from JSON. Validates every node type and edge endpoint BEFORE applying; rejects unknown types or dangling edges without touching the model.',
    parameters: {
      type: 'object',
      properties: {
        nodes: { type: 'array', items: { type: 'object' } },
        edges: { type: 'array', items: { type: 'object' } },
      },
      required: ['nodes'],
      additionalProperties: false,
    },
  },
];

/**
 * Navigation / selection / UI tool descriptors. Advertised ONLY when the hook
 * supplies a `view` access surface (i.e. inside the live React editor). They do
 * not mutate the serialized model — they move the camera or change which
 * element is selected / which modal is open, so the user can SEE what the
 * assistant is referring to.
 */
const VIEW_TOOL_DESCRIPTORS: EditorToolDescriptor[] = [
  {
    name: 'get_ui_state',
    description:
      'Return the current editor UI state: active process, all process summaries (id/name), the current selection (node/edge/swimlane/none), the React Flow viewport (x,y,zoom) and the advanced-modal state. Read-only.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'set_viewport',
    description:
      'Pan/zoom the canvas to an exact React Flow viewport. `zoom` must be a positive number. Does not change the model. This is the low-level scroll/zoom primitive — prefer focus_node or fit_view for element-relative navigation.',
    parameters: {
      type: 'object',
      properties: {
        x: { type: 'number' },
        y: { type: 'number' },
        zoom: { type: 'number', description: 'Zoom factor (> 0).' },
        durationMs: { type: 'number', description: 'Optional animation duration in ms.' },
      },
      required: ['x', 'y', 'zoom'],
      additionalProperties: false,
    },
  },
  {
    name: 'fit_view',
    description:
      'Fit the diagram into view. With `nodeIds` it fits just those nodes; without it fits the whole process. Rejects unknown node ids. Does not change the model.',
    parameters: {
      type: 'object',
      properties: {
        nodeIds: { type: 'array', items: { type: 'string' }, description: 'Optional subset of node ids to fit.' },
        padding: { type: 'number', description: 'Padding fraction (0–1), default ~0.1.' },
        durationMs: { type: 'number' },
        minZoom: { type: 'number' },
        maxZoom: { type: 'number' },
      },
      additionalProperties: false,
    },
  },
  {
    name: 'focus_node',
    description:
      'Center the camera on a single node, optionally selecting it so the user sees exactly which node is meant. Rejects unknown node ids. Does not change the model.',
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        select: { type: 'boolean', description: 'Also select the node (default true).' },
        zoom: { type: 'number', description: 'Optional zoom factor (> 0).' },
        durationMs: { type: 'number' },
      },
      required: ['id'],
      additionalProperties: false,
    },
  },
  {
    name: 'set_selection',
    description:
      "Select/highlight one element so the user can see what the assistant means, or clear the selection. `kind` 'none' clears; otherwise `id` is required and must reference an existing node/edge/swimlane. Rejects unknown ids. Does not change the model.",
    parameters: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['node', 'edge', 'swimlane', 'none'] },
        id: { type: 'string', description: 'Required unless kind is "none".' },
      },
      required: ['kind'],
      additionalProperties: false,
    },
  },
  {
    name: 'open_node_editor',
    description:
      "Select a node and open its configuration modal, optionally on a specific tab (e.g. 'properties', 'http', 'routing', 'script', 'errorHandling', 'form'). Rejects unknown node ids. Does not change the model.",
    parameters: {
      type: 'object',
      properties: {
        nodeId: { type: 'string' },
        tabId: { type: 'string', description: "Tab to open, default 'properties'." },
      },
      required: ['nodeId'],
      additionalProperties: false,
    },
  },
  {
    name: 'switch_process',
    description:
      'Switch the active process (the read/mutate tools operate on the active process). Rejects unknown process ids. Optionally fits the new process into view. Does not change the model.',
    parameters: {
      type: 'object',
      properties: {
        processId: { type: 'string' },
        fit: { type: 'boolean', description: 'Fit the switched-to process into view (default true).' },
      },
      required: ['processId'],
      additionalProperties: false,
    },
  },
];

/** Build the human-readable guidelines paragraph from the live registry. */
const buildGuidelines = (nodeTypes: Record<string, NodeTypeConfig>, hasView: boolean): string => {
  const byCategory = new Map<string, string[]>();
  for (const [key, cfg] of Object.entries(nodeTypes)) {
    if (cfg.disabled) continue;
    const list = byCategory.get(cfg.category) || [];
    list.push(key);
    byCategory.set(cfg.category, list);
  }
  const cats = Array.from(byCategory.entries())
    .map(([cat, keys]) => `${cat}: ${keys.join(', ')}`)
    .join('; ');
  return (
    'This is a BPMN-like workflow process editor. The model is a directed graph of nodes ' +
    '(start/end events, user/service/script tasks, gateways, call activities, intermediate ' +
    'and boundary events) connected by sequence-flow edges. Each node has a unique id, a type ' +
    '(use the exact keys from list_node_types), a display label and an optional actorType ' +
    '(swimlane). Tools route through the editor’s real mutation + validation logic: adding a ' +
    'node with an unknown type, connecting non-existent nodes, or replacing the model with ' +
    'malformed JSON is rejected with a reason instead of corrupting the diagram. Always call ' +
    'get_model first to see current ids before updating, deleting, or connecting. ' +
    (hasView
      ? 'Navigation/selection tools (focus_node, set_selection, fit_view, set_viewport, ' +
        'open_node_editor, switch_process, get_ui_state) move the camera or change the on-screen ' +
        'selection WITHOUT mutating the model — use them so the user can SEE the node/edge you mean. ' +
        'switch_process changes which process the read/mutate tools operate on. '
      : '') +
    `Valid node types by category — ${cats}.`
  );
};

// --- Capability factory (pure) ---

export function createWorkflowAiCapability(access: WorkflowModelAccess): EditorAiCapability {
  // Navigation/selection/UI tools are only advertised when the host wired the
  // live view surface (i.e. the React editor). Headless/unit contexts omit it.
  const tools = access.view ? TOOL_DESCRIPTORS.concat(VIEW_TOOL_DESCRIPTORS) : TOOL_DESCRIPTORS;

  /**
   * Gate a mutation behind the editor's REAL validation. Given the candidate
   * next nodes+edges, validate them; if they introduce NO new error-severity
   * issues, apply via the supplied applier and return its ok-result. Otherwise
   * reject with the validator's reason and DO NOT mutate the model.
   */
  const gate = (
    currentNodes: AppNode[],
    currentEdges: Edge[],
    nextNodes: AppNode[],
    nextEdges: Edge[],
    apply: () => void,
    onOk: () => EditorToolResult
  ): EditorToolResult => {
    const baseline = access.validate(currentNodes, currentEdges);
    const candidate = access.validate(nextNodes, nextEdges);
    const introduced = newErrors(baseline, candidate);
    if (introduced.length > 0) {
      return reject(`Change rejected — it would introduce validation errors: ${formatErrors(introduced)}`);
    }
    apply();
    return onOk();
  };

  const invoke = async (toolName: string, rawArgs: unknown): Promise<EditorToolResult> => {
    const args: Record<string, unknown> = isObject(rawArgs) ? rawArgs : {};
    const nodeTypes = access.getNodeTypes();

    switch (toolName) {
      case 'get_model': {
        const nodes = access.getNodes().map(compactNode);
        const edges = access.getEdges().map(compactEdge);
        return ok({ nodes, edges, nodeCount: nodes.length, edgeCount: edges.length });
      }

      case 'list_node_types': {
        const types = Object.entries(nodeTypes)
          .filter(([, cfg]) => !cfg.disabled)
          .map(([key, cfg]) => ({
            type: key,
            label: cfg.label,
            category: cfg.category,
            description: cfg.description,
          }));
        return ok({ types });
      }

      case 'add_node': {
        const type = args.type;
        const label = args.label;
        if (typeof type !== 'string' || !type) return reject('`type` is required and must be a string.');
        if (!nodeTypes[type]) {
          return reject(
            `Unknown node type "${type}". Call list_node_types for valid keys.`
          );
        }
        if (nodeTypes[type].disabled) return reject(`Node type "${type}" is disabled and cannot be added.`);
        if (typeof label !== 'string' || !label.trim()) {
          return reject('`label` is required and must be a non-empty string.');
        }
        let position = { x: 0, y: 0 };
        if (args.position !== undefined) {
          const p = args.position;
          if (!isObject(p) || !isFiniteNumber(p.x) || !isFiniteNumber(p.y)) {
            return reject('`position` must be an object with finite numeric x and y.');
          }
          position = { x: p.x, y: p.y };
        }
        if (args.data !== undefined && !isObject(args.data)) {
          return reject('`data` must be an object when provided.');
        }
        const defaults = access.getDefaultData(type) || {};
        const data: WorkflowNodeData = {
          ...defaults,
          ...(isObject(args.data) ? (args.data as Partial<WorkflowNodeData>) : {}),
          label: label.trim(),
          status: 'idle',
        };
        if (typeof args.actorType === 'string' && args.actorType.trim()) {
          data.actorType = args.actorType.trim();
        }
        const id = nextId('node');
        const newNode: AppNode = { id, type, position, data };
        const curNodes = access.getNodes();
        const curEdges = access.getEdges();
        return gate(
          curNodes,
          curEdges,
          curNodes.concat(newNode),
          curEdges,
          () => access.applyNodes((nds) => nds.concat(newNode)),
          () => ok({ id, type, label: data.label })
        );
      }

      case 'update_node': {
        const id = args.id;
        if (typeof id !== 'string' || !id) return reject('`id` is required.');
        if (!isObject(args.data)) return reject('`data` must be an object.');
        const existing = access.getNodes().find((n) => n.id === id);
        if (!existing) return reject(`No node with id "${id}".`);
        // Reject attempts to mutate structural/identity fields via data merge.
        if ('id' in (args.data as object) || 'type' in (args.data as object)) {
          return reject('`data` may not contain `id` or `type`; use a dedicated path to change identity.');
        }
        const patch = args.data as Partial<WorkflowNodeData>;
        if ('label' in patch && (typeof patch.label !== 'string' || !patch.label.trim())) {
          return reject('`label`, if provided, must be a non-empty string.');
        }
        const applyUpdate = (nds: AppNode[]): AppNode[] =>
          nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
        const curNodes = access.getNodes();
        const curEdges = access.getEdges();
        return gate(
          curNodes,
          curEdges,
          applyUpdate(curNodes),
          curEdges,
          () => access.applyNodes(applyUpdate),
          () => ok({ id })
        );
      }

      case 'delete_node': {
        const id = args.id;
        if (typeof id !== 'string' || !id) return reject('`id` is required.');
        const nodes = access.getNodes();
        if (!nodes.some((n) => n.id === id)) return reject(`No node with id "${id}".`);
        // Cascade: remove the node and any children (boundary events) parented to it.
        const removedIds = new Set<string>([id]);
        nodes.forEach((n) => {
          if (n.parentNode === id) removedIds.add(n.id);
        });
        const filterNodes = (nds: AppNode[]): AppNode[] => nds.filter((n) => !removedIds.has(n.id));
        const filterEdges = (eds: Edge[]): Edge[] =>
          eds.filter((e) => !removedIds.has(e.source) && !removedIds.has(e.target));
        const curEdges = access.getEdges();
        return gate(
          nodes,
          curEdges,
          filterNodes(nodes),
          filterEdges(curEdges),
          () => {
            access.applyNodes(filterNodes);
            access.applyEdges(filterEdges);
          },
          () => ok({ id, removed: Array.from(removedIds) })
        );
      }

      case 'connect_nodes': {
        const source = args.source;
        const target = args.target;
        if (typeof source !== 'string' || !source) return reject('`source` is required.');
        if (typeof target !== 'string' || !target) return reject('`target` is required.');
        if (source === target) return reject('Cannot connect a node to itself.');
        const nodes = access.getNodes();
        if (!nodes.some((n) => n.id === source)) return reject(`No source node with id "${source}".`);
        if (!nodes.some((n) => n.id === target)) return reject(`No target node with id "${target}".`);
        const edges = access.getEdges();
        if (edges.some((e) => e.source === source && e.target === target)) {
          return reject(`An edge from "${source}" to "${target}" already exists.`);
        }
        if (args.label !== undefined && typeof args.label !== 'string') {
          return reject('`label`, if provided, must be a string.');
        }
        const edge: Edge = {
          id: nextId('e'),
          source,
          target,
          type: 'deletable',
          animated: true,
          label: typeof args.label === 'string' ? args.label : undefined,
        };
        return gate(
          nodes,
          edges,
          nodes,
          edges.concat(edge),
          () => access.applyEdges((eds) => eds.concat(edge)),
          () => ok({ id: edge.id, source, target })
        );
      }

      case 'delete_edge': {
        const id = args.id;
        if (typeof id !== 'string' || !id) return reject('`id` is required.');
        const curEdges = access.getEdges();
        if (!curEdges.some((e) => e.id === id)) return reject(`No edge with id "${id}".`);
        const curNodes = access.getNodes();
        const filterEdges = (eds: Edge[]): Edge[] => eds.filter((e) => e.id !== id);
        return gate(
          curNodes,
          curEdges,
          curNodes,
          filterEdges(curEdges),
          () => access.applyEdges(filterEdges),
          () => ok({ id })
        );
      }

      case 'validate': {
        const issues = access.validate(access.getNodes(), access.getEdges()).map(compactIssue);
        const errors = issues.filter((i) => i.severity === 'error').length;
        const warnings = issues.filter((i) => i.severity === 'warning').length;
        return ok({ issues, errors, warnings });
      }

      case 'replace_model': {
        const rawNodes = args.nodes;
        const rawEdges = args.edges ?? [];
        if (!Array.isArray(rawNodes)) return reject('`nodes` must be an array.');
        if (!Array.isArray(rawEdges)) return reject('`edges` must be an array.');

        // Pre-validate everything BEFORE applying so a bad payload never
        // partially corrupts the live model.
        const nextNodes: AppNode[] = [];
        const seenIds = new Set<string>();
        for (let i = 0; i < rawNodes.length; i++) {
          const raw = rawNodes[i];
          if (!isObject(raw)) return reject(`nodes[${i}] is not an object.`);
          const { id, type } = raw as Record<string, unknown>;
          if (typeof id !== 'string' || !id) return reject(`nodes[${i}].id is required.`);
          if (seenIds.has(id)) return reject(`Duplicate node id "${id}".`);
          if (typeof type !== 'string' || !type) return reject(`nodes[${i}].type is required.`);
          if (!nodeTypes[type]) return reject(`nodes[${i}] has unknown type "${type}".`);
          const data = isObject(raw.data) ? (raw.data as Partial<WorkflowNodeData>) : undefined;
          const label =
            data && typeof data.label === 'string' && data.label.trim()
              ? data.label
              : nodeTypes[type].label;
          const pos = isObject(raw.position) ? (raw.position as Record<string, unknown>) : {};
          const position = {
            x: isFiniteNumber(pos.x) ? pos.x : 0,
            y: isFiniteNumber(pos.y) ? pos.y : 0,
          };
          seenIds.add(id);
          nextNodes.push({
            id,
            type,
            position,
            parentNode: typeof raw.parentNode === 'string' ? (raw.parentNode as string) : undefined,
            data: { ...(access.getDefaultData(type) || {}), ...(data || {}), label, status: 'idle' },
          });
        }
        // Validate parent references resolve.
        for (const n of nextNodes) {
          if (n.parentNode && !seenIds.has(n.parentNode)) {
            return reject(`Node "${n.id}" references missing parentNode "${n.parentNode}".`);
          }
        }
        const nextEdges: Edge[] = [];
        const seenEdgeIds = new Set<string>();
        for (let i = 0; i < rawEdges.length; i++) {
          const raw = rawEdges[i];
          if (!isObject(raw)) return reject(`edges[${i}] is not an object.`);
          const { source, target } = raw as Record<string, unknown>;
          if (typeof source !== 'string' || !seenIds.has(source)) {
            return reject(`edges[${i}] has dangling/invalid source "${String(source)}".`);
          }
          if (typeof target !== 'string' || !seenIds.has(target)) {
            return reject(`edges[${i}] has dangling/invalid target "${String(target)}".`);
          }
          const id = typeof raw.id === 'string' && raw.id ? (raw.id as string) : nextId('e');
          if (seenEdgeIds.has(id)) return reject(`Duplicate edge id "${id}".`);
          seenEdgeIds.add(id);
          nextEdges.push({
            id,
            source,
            target,
            type: 'deletable',
            animated: true,
            label: typeof raw.label === 'string' ? (raw.label as string) : undefined,
            sourceHandle: typeof raw.sourceHandle === 'string' ? (raw.sourceHandle as string) : undefined,
            targetHandle: typeof raw.targetHandle === 'string' ? (raw.targetHandle as string) : undefined,
          });
        }

        return gate(
          access.getNodes(),
          access.getEdges(),
          nextNodes,
          nextEdges,
          () => {
            access.applyNodes(() => nextNodes);
            access.applyEdges(() => nextEdges);
          },
          () => ok({ nodeCount: nextNodes.length, edgeCount: nextEdges.length })
        );
      }

      // --- Navigation / selection / UI (only when the host wired `view`) ---

      case 'get_ui_state':
      case 'set_viewport':
      case 'fit_view':
      case 'focus_node':
      case 'set_selection':
      case 'open_node_editor':
      case 'switch_process': {
        const view = access.view;
        if (!view) {
          return reject(`Tool "${toolName}" is unavailable: this editor host did not wire viewport/selection access.`);
        }

        switch (toolName) {
          case 'get_ui_state': {
            return ok({
              processes: view.getProcesses(),
              selection: view.getSelection(),
              viewport: view.getViewport(),
              modal: view.getModalState(),
              swimlanes: view.getSwimlanes().map((s) => ({ id: s.id, name: s.name })),
            });
          }

          case 'set_viewport': {
            if (!isFiniteNumber(args.x) || !isFiniteNumber(args.y)) {
              return reject('`x` and `y` must be finite numbers.');
            }
            if (!isFiniteNumber(args.zoom) || args.zoom <= 0) {
              return reject('`zoom` must be a finite number greater than 0.');
            }
            const duration = isFiniteNumber(args.durationMs) ? args.durationMs : undefined;
            view.setViewport({ x: args.x, y: args.y, zoom: args.zoom }, duration);
            return ok({ x: args.x, y: args.y, zoom: args.zoom });
          }

          case 'fit_view': {
            let nodeIds: string[] | undefined;
            if (args.nodeIds !== undefined) {
              if (!Array.isArray(args.nodeIds) || !args.nodeIds.every((id) => typeof id === 'string')) {
                return reject('`nodeIds` must be an array of strings.');
              }
              const known = new Set(access.getNodes().map((n) => n.id));
              const missing = (args.nodeIds as string[]).filter((id) => !known.has(id));
              if (missing.length > 0) return reject(`Unknown node id(s): ${missing.join(', ')}.`);
              nodeIds = args.nodeIds as string[];
            }
            if (args.padding !== undefined && !isFiniteNumber(args.padding)) {
              return reject('`padding` must be a finite number.');
            }
            view.fitView({
              nodeIds,
              padding: isFiniteNumber(args.padding) ? args.padding : undefined,
              durationMs: isFiniteNumber(args.durationMs) ? args.durationMs : undefined,
              minZoom: isFiniteNumber(args.minZoom) ? args.minZoom : undefined,
              maxZoom: isFiniteNumber(args.maxZoom) ? args.maxZoom : undefined,
            });
            return ok({ fitted: nodeIds ? nodeIds.length : 'all' });
          }

          case 'focus_node': {
            const id = args.id;
            if (typeof id !== 'string' || !id) return reject('`id` is required.');
            if (!access.getNodes().some((n) => n.id === id)) return reject(`No node with id "${id}".`);
            if (args.zoom !== undefined && (!isFiniteNumber(args.zoom) || args.zoom <= 0)) {
              return reject('`zoom`, if provided, must be a finite number greater than 0.');
            }
            const duration = isFiniteNumber(args.durationMs) ? args.durationMs : undefined;
            const select = args.select === undefined ? true : args.select === true;
            view.centerNode(id, isFiniteNumber(args.zoom) ? args.zoom : undefined, duration);
            if (select) view.selectNode(id);
            return ok({ id, selected: select });
          }

          case 'set_selection': {
            const kind = args.kind;
            if (kind !== 'node' && kind !== 'edge' && kind !== 'swimlane' && kind !== 'none') {
              return reject('`kind` must be one of "node", "edge", "swimlane", "none".');
            }
            if (kind === 'none') {
              view.clearSelection();
              return ok({ kind: 'none' });
            }
            const id = args.id;
            if (typeof id !== 'string' || !id) return reject('`id` is required unless kind is "none".');
            if (kind === 'node') {
              if (!access.getNodes().some((n) => n.id === id)) return reject(`No node with id "${id}".`);
              view.selectNode(id);
            } else if (kind === 'edge') {
              if (!access.getEdges().some((e) => e.id === id)) return reject(`No edge with id "${id}".`);
              view.selectEdge(id);
            } else {
              if (!view.getSwimlanes().some((s) => s.id === id)) return reject(`No swimlane with id "${id}".`);
              view.selectSwimlane(id);
            }
            return ok({ kind, id });
          }

          case 'open_node_editor': {
            const nodeId = args.nodeId;
            if (typeof nodeId !== 'string' || !nodeId) return reject('`nodeId` is required.');
            if (!access.getNodes().some((n) => n.id === nodeId)) return reject(`No node with id "${nodeId}".`);
            if (args.tabId !== undefined && (typeof args.tabId !== 'string' || !args.tabId)) {
              return reject('`tabId`, if provided, must be a non-empty string.');
            }
            const tabId = typeof args.tabId === 'string' ? args.tabId : undefined;
            view.selectNode(nodeId);
            view.openNodeEditor(nodeId, tabId);
            return ok({ nodeId, tabId: tabId ?? 'properties' });
          }

          case 'switch_process': {
            const processId = args.processId;
            if (typeof processId !== 'string' || !processId) return reject('`processId` is required.');
            const procs = view.getProcesses();
            const target = procs.find((p) => p.id === processId);
            if (!target) return reject(`No process with id "${processId}".`);
            if (!target.active) view.switchProcess(processId);
            if (args.fit === undefined || args.fit === true) view.fitView({});
            return ok({ processId, name: target.name });
          }

          default:
            return reject(`Unknown tool "${toolName}".`);
        }
      }

      default:
        return reject(`Unknown tool "${toolName}".`);
    }
  };

  return {
    kind: 'workflow',
    guidelines: buildGuidelines(access.getNodeTypes(), !!access.view),
    tools,
    invoke,
  };
}
